import Image, { type StaticImageData } from "next/image";
import { type CSSProperties } from "react";
import { Minus, Plus } from "lucide-react";

import { cn } from "@/lib/utils";

export type Hotspot = {
  id: string;
  /** Position as percentages of the image box. */
  x: number;
  y: number;
  label: string;
};

type HotspotImageProps = {
  image: StaticImageData | string;
  alt: string;
  hotspots: readonly Hotspot[];
  selectedId?: string;
  onHotspotClick?: (id: string) => void;
  imageStyle?: CSSProperties;
  imageSizes?: string;
  hotspotClassName?: string;
  className?: string;
};

const HotspotImage = ({
  image,
  alt,
  hotspots,
  selectedId,
  onHotspotClick,
  imageStyle,
  imageSizes = "100vw",
  hotspotClassName,
  className,
}: HotspotImageProps) => (
  <div className={cn("relative", className)}>
    <span
      className={cn("absolute block", imageStyle === undefined && "inset-0")}
      style={imageStyle}
    >
      <Image
        src={image}
        alt={alt}
        fill
        sizes={imageSizes}
        className="object-contain"
      />
    </span>
    {hotspots.map((hotspot) => {
      const selected = hotspot.id === selectedId;

      return (
        <button
          key={hotspot.id}
          type="button"
          aria-label={hotspot.label}
          aria-pressed={selected}
          onClick={() => onHotspotClick?.(hotspot.id)}
          className={cn(
            "absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-colors duration-quick motion-reduce:transition-none",
            // Figma's Plus Icon (10570-54126) has three states: rose/plus,
            // rose-deep/plus on hover, orange/minus on click. Click is our
            // selected state, so a selected marker has nowhere left to go.
            selected
              ? "bg-brand-orange text-content-inverse"
              : "bg-brand-rose text-brand-black hover:bg-brand-rose-deep",
            hotspotClassName,
          )}
          style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
        >
          {selected ? (
            <Minus aria-hidden className="h-4 w-4" />
          ) : (
            <Plus aria-hidden className="h-4 w-4" />
          )}
        </button>
      );
    })}
  </div>
);

export default HotspotImage;
